'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {mediaFromWire, type OverviewStatsWireSection} from '../../model';
import {RichContent} from '../article';
import {Section} from '../primitives';
import {EyebrowHeading} from './EyebrowHeading';
import {OVERVIEW_ASPECT, SectionMedia} from './SectionMedia';
import {StatItem} from './StatItem';

export interface AboutOverviewStatisticsSectionProps {
  title: string;
  overview: OverviewStatsWireSection['overview'];
  sectionId?: string;
}

export function AboutOverviewStatisticsSection({title, overview, sectionId}: AboutOverviewStatisticsSectionProps) {
  const media = mediaFromWire(overview?.media);
  const eyebrow = overview?.displayLabel;
  const description = overview?.description;
  if (!media || !title || !description) {
    return null;
  }
  const stats = (overview.statistics ?? []).map(stat => ({label: stat.name, value: stat.displayValue}));
  return (
    <>
      <Section sx={{background: theme => theme.palette.colors.surface.section.emphasis, paddingBlock: {xs: 4, md: 9}}}>
        <Container>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'},
              alignItems: {md: 'center'},
              gap: {xs: 3, md: 13},
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5, flex: {md: 1}}}>
              <EyebrowHeading
                eyebrow={eyebrow}
                title={title}
                eyebrowColor={theme => theme.palette.colors.text.primary.inverse}
                titleColor={theme => theme.palette.colors.text.neutral.inverse}
              />
              <Box sx={{maxWidth: 520}}>
                <RichContent html={description} collapsible={false} sx={{color: theme => theme.palette.colors.text.neutral.inverse}} />
              </Box>
            </Box>
            <Box sx={{order: {xs: -1, md: 0}, flex: {md: 1.15}}}>
              <SectionMedia
                media={media}
                alt={media.alt ?? title}
                aspectRatio={OVERVIEW_ASPECT}
                height={{xs: 310, md: 'auto'}}
                sectionName={title}
                sectionId={sectionId}
              />
            </Box>
          </Box>
        </Container>
      </Section>
      <Section sx={{background: theme => theme.palette.colors.surface.section.emphasisDark, paddingBlock: {xs: 4, md: 5}}}>
        <Container
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'},
            gap: {xs: 4, md: 3},
          }}
        >
          {stats.slice(0, 4).map(stat => (
            <StatItem key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </Container>
      </Section>
    </>
  );
}

AboutOverviewStatisticsSection.displayName = 'AboutOverviewStatisticsSection';
